<template>
  <div
    class="rs-active-filters-list"
    :class="[
      cards
        ? 'tw-flex tw-flex-col tw-gap-2'
        : 'sf-tab sf-tab-full sf-bg-snow tw-p-4 tw-rounded-lg',
      { 'sf-pointer': editable },
    ]"
    data-testid="active-filters-list"
  >
    <div
      v-for="filter in filters"
      :key="filter.key"
      :data-filter-type="filter.type"
      class="sf-c-text-darker sf-hover tw-group"
      :class="
        cards
          ? [
              'tw-border tw-border-solid tw-border-sf-neutral-lighter-2',
              'tw-rounded-lg tw-bg-white tw-p-2',
              'hover:tw-bg-sf-snow-light',
            ]
          : 'sf-tab-item sf-pad-0'
      "
      data-testid="active-filter"
      @click="onRowClick(filter)"
    >
      <div class="sf-level sf-level-between sf-pad-1">
        <div class="sf-level-item tw-overflow-hidden">
          <div>{{ filterName(filter) }}</div>
          <div
            v-if="!filterDropdownValuesLoading"
            class="sf-font-light tw-truncate"
          >
            {{ activeFilterSubtitle(filter) }}
          </div>
        </div>
        <div
          class="
            sf-level-item
            tw-min-w-9 tw-flex tw-items-center tw-justify-end
          "
        >
          <template v-if="usingNewLayout">
            <SfButtonDs
              v-for="action in newLayoutActions(filter)"
              :key="action.key"
              v-tooltip="action.tooltip"
              :aria-label="action.tooltip"
              variant="tertiary"
              size="sm"
              icon-only
              :icon-before="action.icon"
              class="tw-invisible group-hover:tw-visible !tw-w-8 !tw-h-8"
              :data-testid="action.testid"
              @click.stop.prevent="action.handler"
            />
          </template>
          <i
            v-else-if="!immutable"
            data-testid="remove-filter"
            class="
              fa fa-regular fa-trash-can
              sf-c-text-highlight sf-bg-snow sf-hover-item sf-dim-el-hover
              tw-cursor-pointer
            "
            @click.stop.prevent="removeFilter(filter)"
          ></i>
          <i
            v-if="hasError(filter)"
            v-tooltip="
              I18n.t('companies_overview.filters.filter_error_tooltip', {
                count: errorCount(filter),
              })
            "
            class="
              fa fa-solid fa-triangle-exclamation
              tw-pl-2 tw-text-sf-danger-light-2
            "
          ></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { useCompaniesStore } from '@app/insights/store/companies'
import { useFiltersStore } from '@app/insights/store/filters'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import readableColumnName from '../../utils/readable-column-name'

export default {
  components: {
    SfButtonDs,
  },

  props: {
    editFilter: {
      type: Function,
      required: true,
    },

    immutable: {
      type: Boolean,
      required: false,
      default: false,
    },

    // When false, clicking a filter row does nothing (removal is still possible
    // via the trash icon).
    editable: {
      type: Boolean,
      required: false,
      default: true,
    },

    // Render each filter as its own bordered white card (hover to #fafafa) with
    // vertical spacing between them, instead of a single grey list. Used by the
    // applied-filters overview modal.
    cards: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  computed: {
    ...mapPiniaState(useCompaniesStore, ['errorCountsPerFilter']),
    ...mapPiniaState(useFiltersStore, [
      'allFiltersByType',
      'filterDropdownValuesLoading',
      'getActiveFilters',
    ]),
    filters() {
      return this.getActiveFilters.map((filter) => ({
        ...filter,
        ...this.allFiltersByType[filter.type],
        key: [filter.nameKey, filter.uuid].join('_'),
      }))
    },
    usingNewLayout() {
      return isUsingNewLayout()
    },
  },

  methods: {
    ...mapPiniaActions(useFiltersStore, ['activeFilterSubtitle']),
    ...mapPiniaActions(useFiltersStore, { removeFilter: 'removeActiveFilter' }),

    onRowClick(filter) {
      if (this.editable) this.editFilter(filter)
    },
    // New-layout-only Edit/Delete tertiary icon buttons, in display order.
    newLayoutActions(filter) {
      return [
        {
          key: 'edit',
          show: this.editable,
          icon: 'pen',
          tooltip: I18n.t('common.edit'),
          testid: 'edit-filter',
          handler: () => this.onRowClick(filter),
        },
        {
          key: 'remove',
          show: !this.immutable,
          icon: 'trash-alt',
          tooltip: I18n.t('common.remove'),
          testid: 'remove-filter',
          handler: () => this.removeFilter(filter),
        },
      ].filter((action) => action.show)
    },
    filterName(filter) {
      const { columnName } = filter
      return readableColumnName(columnName, filter.nameKey)
    },
    errorCount(filter) {
      return this.errorCountsPerFilter[filter.uuid] || 0
    },
    hasError(filter) {
      return this.errorCount(filter) > 0
    },
  },
}
</script>
